import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import {
  checkPermission,
  formatDateTime,
  getDefaultSeason,
  getTimezoneDate,
  globalStatuses,
} from '../../utilities/helpers'
import { buildFlexPackAssignmentModalPayload } from '../../utilities/flexPackAssignment'
import { buildAccessPassAssignmentModalPayload } from '../../utilities/accessPassAssignment'

import { getAssignableInventory, getSeasons } from '../../utilities/api'
import { useModal } from '../../providers/ModalProvider/ModalProvider'
import AssignTicketsModalContentV2 from '../../providers/AssignToProvider/AssignTicketsModalContent/AssignTicketsModalContentV2'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'

import { SeasonSelector } from '../SeasonSelector'
import { LoadingContainer } from '../LoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import placeholder from '../../assets/placeholder.png'
import './assignInventoryWrapper.scss'

const hasAssignmentPermission = (getPermissions) =>
  [
    ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT,
    ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT,
    ORGANIZATION_PERMISSION_SLUGS.FLEX_PACKAGES_MANAGEMENT,
  ].some((slug) => checkPermission(getPermissions(), slug))

export default function AssignInventoryWrapper({ organization }) {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const { orgPermissions } = useContext(UserContext)

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady && hasAssignmentPermission(AuthService.getPermissions)

  const [isLoading, setIsLoading] = useState(true)
  const [seasonsLoaded, setSeasonsLoaded] = useState(false)
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const [activeTab, setActiveTab] = useState('events')
  const [inventory, setInventory] = useState({
    events: [],
    packages: [],
    flexPacks: [],
    accessPassTemplates: [],
  })

  const hasSeasons = seasons.length > 0

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission || !organization?.uuid) return

    let cancelled = false
    const load = async () => {
      try {
        const seasonRes = await getSeasons(organization.uuid)
        if (cancelled) return

        const loadedSeasons = Array.isArray(seasonRes?.data)
          ? seasonRes.data
          : []
        setSeasons(loadedSeasons)

        const defaultSeason = getDefaultSeason(loadedSeasons)
        const seasonId = defaultSeason?.id
          ? String(defaultSeason.id)
          : loadedSeasons[0]?.id
            ? String(loadedSeasons[0].id)
            : ''
        setSelectedSeason(seasonId)
      } catch (error) {
        console.error('Error loading seasons for assign inventory:', error)
      } finally {
        // Don't toggle isLoading here — the inventory effect below owns the
        // spinner so we never flash the empty state between the two fetches.
        if (!cancelled) setSeasonsLoaded(true)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission || !organization?.uuid) return
    if (!seasonsLoaded) return
    if (hasSeasons && !selectedSeason) return

    let cancelled = false
    const loadInventory = async () => {
      setIsLoading(true)
      try {
        const seasonIdParam = hasSeasons ? selectedSeason : undefined
        const res = await getAssignableInventory(
          organization.uuid,
          seasonIdParam
        )
        if (cancelled) return
        setInventory({
          events: Array.isArray(res?.data?.events) ? res.data.events : [],
          packages: Array.isArray(res?.data?.packages) ? res.data.packages : [],
          flexPacks: Array.isArray(res?.data?.flexPacks)
            ? res.data.flexPacks
            : [],
          accessPassTemplates: Array.isArray(res?.data?.accessPassTemplates)
            ? res.data.accessPassTemplates
            : [],
        })
      } catch (error) {
        console.error('Error loading assignable inventory:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadInventory()
    return () => {
      cancelled = true
    }
  }, [
    orgPermissionsReady,
    hasPermission,
    organization?.uuid,
    selectedSeason,
    hasSeasons,
    seasonsLoaded,
  ])

  const emptyState = useMemo(
    () =>
      inventory.events.length === 0 &&
      inventory.packages.length === 0 &&
      inventory.flexPacks.length === 0 &&
      inventory.accessPassTemplates.length === 0,
    [inventory]
  )

  const openFlexAssignModal = (flexPack) => {
    openModal(
      AssignTicketsModalContentV2,
      buildFlexPackAssignmentModalPayload({ flexPack, organization }),
      { className: 'modal-xl', contentClassName: '' }
    )
  }

  const openAccessPassAssignModal = (template) => {
    openModal(
      AssignTicketsModalContentV2,
      buildAccessPassAssignmentModalPayload({
        accessPassTemplate: template,
        organization,
      }),
      { className: 'modal-xl', contentClassName: '' }
    )
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  return (
    <section
      className="spacer-sm"
      data-testid="assign-inventory-wrapper-content"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Assign Inventory</h1>
        </div>
        <p className="section-header-desc">
          Assign event tickets, season packages, flex packs, and access passes
          to a buyer.
        </p>
      </header>

      {seasonsLoaded && hasSeasons && (
        <div className="mb-4">
          <SeasonSelector
            seasons={seasons}
            selected={selectedSeason}
            setSelected={setSelectedSeason}
            isLoading={isLoading}
          />
        </div>
      )}

      {seasonsLoaded && !hasSeasons && (
        <Card body className="mb-4">
          <p className="mb-0">
            No seasons were found for this organization. Showing all assignable
            events, packages, flex packs, and access passes.
          </p>
        </Card>
      )}

      {isLoading ? (
        <LoadingContainer />
      ) : emptyState ? (
        <Card body>
          <EmptyContainer style="center lg">
            <p>No assignable inventory found.</p>
          </EmptyContainer>
        </Card>
      ) : (
        <Tabs
          id="assign-inventory-tabs"
          variant="pills"
          activeKey={activeTab}
          onSelect={(tabKey) => setActiveTab(tabKey || 'events')}
          className="w-max-content nav-pills-default assign-inventory-tabs"
        >
          <Tab eventKey="events" title="Events">
            <div className="mt-3">
              <InventoryList
                title="Events"
                items={inventory.events}
                onAssign={(item) =>
                  navigate(`/event/${item.uuid}/seatmap/assign`)
                }
                renderItem={(item) => (
                  <EventInventoryRow
                    key={`event-${item.id}`}
                    item={item}
                    onAssign={navigate}
                  />
                )}
                organization={organization}
              />
            </div>
          </Tab>
          <Tab eventKey="packages" title="Season Packages">
            <div className="mt-3">
              <InventoryList
                title="Season Packages"
                items={inventory.packages}
                actionLabel="Assign on seatmap"
                onAssign={(item) =>
                  navigate(`/package/${item.uuid}/seatmap/assign`)
                }
                organization={organization}
              />
            </div>
          </Tab>
          <Tab eventKey="flexPacks" title="Flex Packs">
            <div className="mt-3">
              <InventoryList
                title="Flex Packs"
                items={inventory.flexPacks}
                actionLabel="Assign flex pack"
                onAssign={(item) => openFlexAssignModal(item)}
                organization={organization}
              />
            </div>
          </Tab>
          <Tab eventKey="accessPasses" title="Access Passes">
            <div className="mt-3">
              <InventoryList
                title="Access Passes"
                items={inventory.accessPassTemplates}
                actionLabel="Assign access pass"
                onAssign={(item) => openAccessPassAssignModal(item)}
                organization={organization}
                renderItem={(item) => (
                  <AccessPassInventoryRow
                    key={`access-pass-${item.id}`}
                    item={item}
                    onAssign={openAccessPassAssignModal}
                    organization={organization}
                  />
                )}
              />
            </div>
          </Tab>
        </Tabs>
      )}
    </section>
  )
}

function InventoryList({
  title,
  items,
  actionLabel,
  onAssign,
  renderItem,
  organization,
}) {
  return (
    <Card body className="card--md assign-inventory-panel">
      <div className="section-header-sm section-heading section-heading--primary">
        <Card.Title as="h1" className="card-title-lg">
          {title}
        </Card.Title>
      </div>
      {items.length === 0 ? (
        <p className="text-muted mb-0">No {title.toLowerCase()} available.</p>
      ) : (
        <Card body className="card--sm card--dark assign-inventory-group">
          <Stack as="ul" gap={2} className="assign-inventory-list">
            {items.map((item) =>
              renderItem ? (
                renderItem(item)
              ) : (
                <ProductInventoryRow
                  key={`${title}-${item.id}`}
                  item={item}
                  actionLabel={actionLabel}
                  onAssign={onAssign}
                  organization={organization}
                />
              )
            )}
          </Stack>
        </Card>
      )}
    </Card>
  )
}

function EventInventoryRow({ item, onAssign }) {
  const status = resolveEventStatus(item)
  const showStatusBadge = String(status?.label || '').toLowerCase() !== 'draft'
  const timezone = item?.venue?.timezone
  const eventDate = item?.start

  return (
    <li key={`event-${item.id}`} className="assign-inventory-item">
      <Card className="card--sm assign-inventory-item-card">
        <Card.Body className="flex gap-3 align-items-center">
          <Stack direction="horizontal" gap={3} className="flex-grow-1">
            <Card.Img
              src={item?.image?.url || placeholder}
              width="80"
              height="80"
              className="event-image card-img-sm"
              alt={`Cover art for ${item?.name || 'event'}`}
            />
            <Stack className="justify-content-around">
              <Card.Title as="h5">{item?.name}</Card.Title>
              <Card.Subtitle
                as="h6"
                className="m-0 subtitle-bolder subtitle--dark"
              >
                {item?.venue?.name || 'Unknown venue'}
              </Card.Subtitle>
              <Card.Text className="text-muted small">
                <span>
                  <span>
                    {formatDateTime(
                      getTimezoneDate(eventDate, timezone),
                      'dateOnly'
                    )}
                  </span>
                  <span className="time">
                    {formatDateTime(
                      getTimezoneDate(eventDate, timezone),
                      'timeOnly'
                    )}
                  </span>
                </span>
              </Card.Text>
            </Stack>
          </Stack>
          <Stack
            direction="horizontal"
            gap={3}
            className="ms-auto align-items-center"
          >
            {showStatusBadge && (
              <Badge bg="none" className={`badge--${status?.type}`}>
                {status?.label}
              </Badge>
            )}
            <Button
              size="sm"
              variant="secondary"
              className="assign-inventory-action-btn"
              onClick={() => onAssign(`/event/${item.uuid}/seatmap/assign`)}
            >
              Assign on seatmap
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    </li>
  )
}

function resolveEventStatus(event) {
  let status = event?.status
  if (status === 'unpublished') {
    status = 'draft'
  } else if (event?.isSoldOut && status !== 'complete') {
    status = 'sold_out'
  }
  return (
    globalStatuses[status] || { label: status || 'Unknown', type: 'inactive' }
  )
}

function ProductInventoryRow({ item, actionLabel, onAssign, organization }) {
  const timezone = item?.venue?.timezone
  const dateSource = item?.start || item?.end
  const subtitle = item?.venue?.name || organization?.name || 'Unknown'

  return (
    <li className="assign-inventory-item">
      <Card className="card--sm assign-inventory-item-card">
        <Card.Body className="flex gap-3 align-items-center">
          <Stack direction="horizontal" gap={3} className="flex-grow-1">
            <Card.Img
              src={item?.image?.url || placeholder}
              width="80"
              height="80"
              className="event-image card-img-sm"
              alt={`Cover art for ${item?.name || 'item'}`}
            />
            <Stack className="justify-content-around">
              <Card.Title as="h5">{item?.name}</Card.Title>
              <Card.Subtitle
                as="h6"
                className="m-0 subtitle-bolder subtitle--dark"
              >
                {subtitle}
              </Card.Subtitle>
              {dateSource && (
                <Card.Text className="text-muted small">
                  <span>
                    <span>
                      {formatDateTime(
                        getTimezoneDate(dateSource, timezone),
                        'dateOnly'
                      )}
                    </span>
                    <span className="time">
                      {formatDateTime(
                        getTimezoneDate(dateSource, timezone),
                        'timeOnly'
                      )}
                    </span>
                  </span>
                </Card.Text>
              )}
            </Stack>
          </Stack>
          <Button
            size="sm"
            variant="secondary"
            className="assign-inventory-action-btn"
            onClick={() => onAssign(item)}
          >
            {actionLabel}
          </Button>
        </Card.Body>
      </Card>
    </li>
  )
}

function AccessPassInventoryRow({ item, onAssign, organization }) {
  const eventCount = item?.events?.length || 0
  const subtitle =
    item?.venue?.name ||
    organization?.name ||
    `${eventCount} event${eventCount === 1 ? '' : 's'}`

  return (
    <li className="assign-inventory-item">
      <Card className="card--sm assign-inventory-item-card">
        <Card.Body className="flex gap-3 align-items-center">
          <Stack direction="horizontal" gap={3} className="flex-grow-1">
            <Card.Img
              src={item?.image?.url || item?.artwork?.url || placeholder}
              width="80"
              height="80"
              className="event-image card-img-sm"
              alt={`Cover art for ${item?.name || 'access pass'}`}
            />
            <Stack className="justify-content-around">
              <Card.Title as="h5">{item?.name}</Card.Title>
              <Card.Subtitle
                as="h6"
                className="m-0 subtitle-bolder subtitle--dark"
              >
                {subtitle}
              </Card.Subtitle>
              <Card.Text className="text-muted small">
                {eventCount} event{eventCount === 1 ? '' : 's'} included
              </Card.Text>
            </Stack>
          </Stack>
          <Button
            size="sm"
            variant="secondary"
            className="assign-inventory-action-btn"
            onClick={() => onAssign(item)}
          >
            Assign access pass
          </Button>
        </Card.Body>
      </Card>
    </li>
  )
}
